import type { Exercise } from "./schemas/exercise.js";
import type { AthleteTargets, Program } from "./schemas/program.js";
import type { TimedSessionResult } from "./progressTypes.js";
import { programWeeklyPrescription } from "./programPrescription.js";
import { computeWeeklyTargets, zoneFor, type TargetZone } from "./weeklyTargets.js";
import { weekWindowStart } from "./weekWindow.js";
import { DAY_MS } from "./freshness.js";
import { pickAuthoredText } from "./authoredText.js";
import type { Locale } from "./locale.js";

// CO-8 coach dashboard — the pure team-overview aggregation. Given each assigned
// athlete's already-fetched bundle (session-results + their catalog + their
// programs + display name/config, all read-only via the CO-11 coach gate), it
// derives ONE card per athlete and the KPI-strip counters, sorted attention-first.
//
// Adherence is DERIVED from computeWeeklyTargets' own zone verdict (a targeted
// muscle in-band-or-over = "hit"), never a parallel percentage formula — so the
// coach's adherence can't drift from the athlete's own Home meter (§CONSOLIDAMENTO
// "two calcs, two numbers"). Exposes an "adherence bar" as an actual-vs-target
// pair the host feeds straight into <TargetBar> (100 = full adherence).

// No session in this many days ⇒ the athlete reads as "stale" (a data fact the
// coach interprets — not a judgement JL makes).
const STALE_DAYS = 10;
// Adherence band around full (100%): ≥90% of targets hit reads "on track".
// Exported so the dashboard's <TargetBar> draws its band from the SAME numbers
// that score adherenceZone here — a second copy could silently disagree.
export const ADHERENCE_BAND_LOW = 90;
export const ADHERENCE_BAND_HIGH = 110;

export type CoachAthleteInput = {
  sub: string;
  name: string;
  weekStartDay?: number | undefined;
  currentProgramId?: string | undefined;
  sessions: TimedSessionResult[];
  catalog: ReadonlyArray<Exercise>;
  programs: ReadonlyArray<Program>;
  // ME-3 — the athlete's own weekly targets (AthleteTargets). The adherence
  // score is against these now (the pinned program stays for the block name +
  // prescription). Absent/null → no scoreable target (degraded card); the
  // program's embedded targets are not read.
  athleteTargets?: AthleteTargets | null | undefined;
};

export type CoachAthleteFlag =
  | "no-program"
  | "stale"
  | "under-target"
  | "on-track";

export type CoachAthleteCard = {
  sub: string;
  name: string;
  // The pinned "active block" program name, or null when nothing pinned.
  blockName: string | null;
  lastSessionAtMs: number | null;
  daysSinceLast: number | null;
  sessionsThisWeek: number;
  // % of targeted muscles hit (in-band or over). Null in degraded mode
  // (no scoreable target) or with no pinned program.
  adherencePct: number | null;
  adherenceZone: TargetZone | null;
  targetCount: number;
  flags: CoachAthleteFlag[];
  // Higher = needs the coach's attention sooner. Drives the list sort.
  priority: number;
};

export type CoachKpis = {
  assigned: number;
  activeThisWeek: number;
  attention: number;
  avgAdherencePct: number | null;
  sessionsThisWeek: number;
};

export type CoachDashboardData = {
  cards: CoachAthleteCard[];
  kpis: CoachKpis;
};

const buildCard = (
  a: CoachAthleteInput,
  nowMs: number,
  locale?: Locale,
): CoachAthleteCard => {
  const pinned =
    (a.currentProgramId
      ? a.programs.find((p) => p.id === a.currentProgramId)
      : null) ?? null;
  const prescription = pinned
    ? programWeeklyPrescription(pinned, a.catalog)
    : null;
  const targets = computeWeeklyTargets(
    a.sessions,
    a.catalog,
    pinned,
    nowMs,
    prescription,
    { weekStartDay: a.weekStartDay },
    a.athleteTargets ?? null,
  );

  const lastSessionAtMs = a.sessions.reduce<number | null>(
    (acc, s) => (acc == null || s.startedAtMs > acc ? s.startedAtMs : acc),
    null,
  );
  const daysSinceLast =
    lastSessionAtMs == null
      ? null
      : Math.floor((nowMs - lastSessionAtMs) / DAY_MS);
  const weekStart = weekWindowStart(nowMs, a.weekStartDay);
  const sessionsThisWeek = a.sessions.filter(
    (s) => s.startedAtMs >= weekStart,
  ).length;

  // Adherence from the meter's own zones: a targeted muscle counts as "hit"
  // when in-band OR over (they trained at least the prescribed volume).
  const targetedRows = (targets?.rows ?? []).filter((r) => r.target != null);
  const targetCount = targets?.targetCount ?? 0;
  const hitCount = targetedRows.filter(
    (r) => r.zone === "in-band" || r.zone === "over",
  ).length;
  const anyUnder = targetedRows.some((r) => r.zone === "under");
  const adherencePct =
    targetCount > 0 ? Math.round((100 * hitCount) / targetCount) : null;
  const adherenceZone =
    adherencePct == null
      ? null
      : zoneFor(adherencePct, ADHERENCE_BAND_LOW, ADHERENCE_BAND_HIGH);

  const stale = daysSinceLast == null || daysSinceLast > STALE_DAYS;
  const flags: CoachAthleteFlag[] = [];
  if (!pinned) flags.push("no-program");
  if (stale) flags.push("stale");
  if (anyUnder) flags.push("under-target");
  if (!stale && !anyUnder && targetCount > 0) flags.push("on-track");

  // Attention weight — additive so "stale AND under target" sorts above either
  // alone. on-track adds nothing (it's the calm state).
  const priority =
    (stale ? 3 : 0) + (anyUnder ? 2 : 0) + (!pinned ? 1 : 0);

  return {
    sub: a.sub,
    name: a.name,
    blockName: pickAuthoredText(pinned?.name, locale, pinned?.authorLocale),
    lastSessionAtMs,
    daysSinceLast,
    sessionsThisWeek,
    adherencePct,
    adherenceZone,
    targetCount,
    flags,
    priority,
  };
};

const buildKpis = (cards: ReadonlyArray<CoachAthleteCard>): CoachKpis => {
  const activeThisWeek = cards.filter((c) => c.sessionsThisWeek > 0).length;
  const attention = cards.filter((c) => c.priority > 0).length;
  const sessionsThisWeek = cards.reduce((n, c) => n + c.sessionsThisWeek, 0);
  const scored = cards.filter((c) => c.adherencePct != null);
  const avgAdherencePct =
    scored.length > 0
      ? Math.round(
          scored.reduce((n, c) => n + (c.adherencePct ?? 0), 0) / scored.length,
        )
      : null;
  return {
    assigned: cards.length,
    activeThisWeek,
    attention,
    avgAdherencePct,
    sessionsThisWeek,
  };
};

export const buildCoachDashboard = (
  inputs: ReadonlyArray<CoachAthleteInput>,
  nowMs: number,
  // Reader's language (PE-21): the program's authored text is resolved
  // reader → author → any available. Omitted → the chain starts at the
  // author, never at English.
  locale?: Locale,
): CoachDashboardData => {
  const cards = inputs.map((a) => buildCard(a, nowMs, locale));
  // Attention-first; tie-break by staleness (a null last-session — never
  // trained — sorts as most stale).
  cards.sort(
    (a, b) =>
      b.priority - a.priority ||
      (b.daysSinceLast ?? Number.MAX_SAFE_INTEGER) -
        (a.daysSinceLast ?? Number.MAX_SAFE_INTEGER),
  );
  return { cards, kpis: buildKpis(cards) };
};
